import {identity, pick} from 'lodash';
import {type DocumentNode, print} from 'graphql/language';
import gql from 'graphql-tag';
import {
  Plugins,
  Component,
  Expressions,
  EvalEnv,
  matchExpression,
} from '@packages/systems/wfdl';
import type {CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import type {BrowserEnvironment} from '@packages/systems/core-constants';
import type {
  DataAdapter,
  DynamoData,
  PaginationPageNumbers,
  QueryVariables,
  RawData,
} from '../types';
import {createQueryBuildersForClient} from '../core/QueryBuilder';
import {memoize, weakMemo} from '@packages/utilities/memo';
import {
  isDynamoWrapper,
  isSearchResultWrapper,
  isCommerceOrderWrapperElement,
} from '@packages/utilities/elementTypeUtils';
import {
  EHole,
  Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {setDynamoEnv} from '../dynamo-env';
import {noopQueryInfo} from '../utils/noopQueryInfo';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import {Qualified} from '@packages/systems/wfdl-Qualified';
import {findDefinedQueryVariableNames} from './findDefinedQueryVariableNames';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {getElementAndComponentTypeByRendererNativeIdPath} from '../utils/getElementAndComponentTypeByRendererNativeIdPath';

export const createClientDynamoDataProvider = ({
  collections,
  mockPageItemData,
  itemId,
  page,
  paginationPageNumbers,
  plugins,
  publishTarget,
  runtimeEnvironment,
  showSampleSearchResults,
  timezone,
  cmsLocaleId,
  focusedSymbolNativeIdPath,
  rootComponentType,
}: {
  collections: CollectionsType;
  mockPageItemData: {[key: string]: unknown} | null;
  itemId: string | null | undefined;
  page: PageRecordType;
  paginationPageNumbers: PaginationPageNumbers;
  plugins: Plugins.PluginsType;
  publishTarget: string | null | undefined;
  runtimeEnvironment: BrowserEnvironment;
  showSampleSearchResults: boolean;
  timezone: string;
  cmsLocaleId?: string | null | undefined;
  focusedSymbolNativeIdPath: NativeId[];
  rootComponentType: Qualified<Component.ComponentName>;
}): DynamoData => {
  return memoizedGetQueryProvider(
    collections,
    itemId,
    mockPageItemData,
    page,
    paginationPageNumbers,
    plugins,
    publishTarget,
    runtimeEnvironment,
    showSampleSearchResults,
    timezone,
    focusedSymbolNativeIdPath,
    rootComponentType,
    cmsLocaleId
  );
};

const memoizedGetQueryProvider = memoize(
  (
    collections: CollectionsType,
    itemId: string | null | undefined,
    mockPageItemData: {[key: string]: unknown} | null,
    page: PageRecordType,
    paginationPageNumbers: PaginationPageNumbers,
    plugins: Plugins.PluginsType,
    publishTarget: string | null | undefined,
    runtimeEnvironment: BrowserEnvironment,
    showSampleSearchResults: boolean,
    timezone: string,
    focusedSymbolNativeIdPath: NativeId[],
    rootComponentType: Qualified<Component.ComponentName>,
    cmsLocaleId?: string | null
  ): DynamoData => {
    return {
      setDynamoEnvForElement: (
        _elementId: string,
        _rendererNativeIdPath: NativeId[],
        env: EvalEnv.EvalEnvType
      ) => env,
      getDataForElement: () => null,
      getQueryForElement: (
        elementId: string,
        rendererNativeIdPath: NativeId[],
        componentMap: Component.ComponentMap,
        evalEnv: EvalEnv.EvalEnvType
      ) => {
        const maybeElementAndComponentType =
          getElementAndComponentTypeByRendererNativeIdPath(
            elementId,
            rendererNativeIdPath,
            rootComponentType,
            componentMap
          );

        if (!maybeElementAndComponentType) {
          return noopQueryInfo;
        }

        const {element, componentType} = maybeElementAndComponentType;

        const {adapter, queryBuilders, variables} =
          createQueryBuildersForClient({
            collections,
            componentMap,
            componentType,
            baseRendererNativeIdPath: rendererNativeIdPath,
            element: getTreeForElement(element),
            itemId,
            page,
            paginationPageNumbers,
            plugins,
            publishTarget,
            runtimeEnvironment,
            showSampleSearchResults,
            timezone,
            evalEnv,
            cmsLocaleId,
            focusedSymbolNativeIdPath,
            rootComponentType,
          });

        const queryBuilder = queryBuilders[0];
        const query = queryBuilder ? queryBuilder(mockPageItemData) : null;

        return {
          query: query ? memoizeFinalQueryAST(query) : query,
          // Designer and Editor have optimizations in place to skip updating or
          // fetching GraphQL queries when the query and the related variables
          // haven't changed. To make those optimizations more efficient we want
          // the dynamo/graphql to only return the variables that the respective
          // query depends on so that a change in irrelevant variables does not
          // affect the optimizations.
          variables: pickOnlyUsedVariables(variables, query),
          adapter: createAdapterFunction(
            element.id,
            rendererNativeIdPath,
            rootComponentType,
            adapter
          ),
        };
      },
    };
  }
);

// `graphql-tag` has memoization so that it returns the same DocumentNode for
// the same query string. Our query generation takes the initially created
// DocumentNode and drops all the invalid fields and unused variable definitions.
// To always return the very same DocumentNode for the same query regardless of
// the invalid query fields or unused variable definitions that the original
// DocumentNode may have had, we use `graphql-tag` to create the DocumentNode
// from the final query string where all the invalid fields and unused variable
// definitions have already been dropped.
const memoizeFinalQueryAST = weakMemo(
  (finalDocumentNode: DocumentNode): DocumentNode => {
    return gql`
      ${print(finalDocumentNode)}
    `;
  }
);

function pickOnlyUsedVariables(
  variables: QueryVariables,
  query: DocumentNode | null
): QueryVariables {
  const usedVariableNames: string[] = query
    ? findDefinedQueryVariableNames(query)
    : [];
  const onlyUsedVariables = pick(variables, usedVariableNames);
  return onlyUsedVariables as QueryVariables;
}

function createAdapterFunction(
  elementId: string,
  rendererNativeIdPath: NativeId[],
  rootComponentType: Qualified<Component.ComponentName>,
  adapter: DataAdapter
) {
  return function adapterFunction(data: RawData) {
    const getDataForElement = adapter(data);
    return {
      data: getDataForElement(
        elementId,
        rendererNativeIdPath,
        rootComponentType
      ),
      setDynamoEnv(env: EvalEnv.EvalEnvType) {
        return setDynamoEnv(
          env,
          getDataForElement(
            elementId,
            rendererNativeIdPath,
            rootComponentType,
            false
          ),
          null
        );
      },
    };
  };
}

const getTreeForElement = (element: {
  data: Expression;
  id: string;
  type: Qualified<Component.ComponentName>;
}) => {
  if (
    isDynamoWrapper(element) ||
    isSearchResultWrapper(element) ||
    isCommerceOrderWrapperElement(element)
  ) {
    // we only want to recurse children for these element types
    return element;
  }

  return removeDescendants(element);
};

function removeDescendants(element: ExpressionElement) {
  const dataExpr = Expressions.getElementData(element);
  const updatedDataExpr = replaceElementsWithHoles(dataExpr);

  if (dataExpr === updatedDataExpr) {
    return element;
  }

  return Expressions.setElementData(element, updatedDataExpr);
}

const replaceElementWithHole = matchExpression<Expression>({
  Element: () => EHole(),
  _: identity,
});

const replaceElementsWithHoles = Expressions.everywhere(replaceElementWithHole);
